import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiCheck, FiChevronDown, FiStar } from 'react-icons/fi';

export type PropDropdownItem =
  | { kind?: 'option'; value: string; label: string; color?: string; starred?: boolean; hint?: string }
  | { kind: 'group'; label: string };

interface PropDropdownProps {
  label: string;
  value: string;
  items: PropDropdownItem[];
  onChange: (value: string) => void;
  /** Shown before the value in the trigger (avatar, status dot…). */
  leading?: React.ReactNode;
  /** Trigger text; defaults to the selected option's label. */
  valueLabel?: string;
  disabled?: boolean;
}

const MENU_GAP = 6;
const MENU_MAX_H = 320;

/**
 * Labelled property dropdown for the command details header (Type /
 * Assignee / Status). Replaces the native <select>, whose open list is drawn
 * by the browser and can't follow the app's theme.
 */
const PropDropdown: React.FC<PropDropdownProps> = ({ label, value, items, onChange, leading, valueLabel, disabled }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; minWidth: number } | null>(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listboxId = useRef(`mc-prop-menu-${Math.random().toString(36).slice(2)}`).current;

  const options = items.filter((i): i is Extract<PropDropdownItem, { value: string }> => i.kind !== 'group');
  const selected = options.find((o) => o.value === value);
  const shownLabel = valueLabel ?? selected?.label ?? value;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const minWidth = Math.max(r.width, 200);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - minWidth - 8);
    setPos(spaceBelow < Math.min(MENU_MAX_H, 240) && r.top > spaceBelow
      ? { left, bottom: window.innerHeight - r.top + MENU_GAP, minWidth }
      : { left, top: r.bottom + MENU_GAP, minWidth });
  }, []);

  const openMenu = () => {
    if (disabled) return;
    place();
    setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value)));
    setOpen(true);
  };
  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }, []);
  const choose = (v: string) => {
    close(true);
    if (v !== value) onChange(v);
  };

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !triggerRef.current?.contains(t)) close();
    };
    const onScrollOrResize = (e: Event) => {
      if (e.type === 'scroll' && menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, close]);

  useEffect(() => {
    if (!open || activeIdx < 0) return;
    menuRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIdx]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(options.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActiveIdx(0); }
    else if (e.key === 'End') { e.preventDefault(); setActiveIdx(options.length - 1); }
    else if ((e.key === 'Enter' || e.key === ' ') && options[activeIdx]) { e.preventDefault(); choose(options[activeIdx].value); }
    else if (e.key === 'Tab') close();
  };

  let optionIdx = -1;
  return (
    <div className={`mc-prop${open ? ' mc-prop--open' : ''}${disabled ? ' mc-prop--disabled' : ''}`}>
      <span className="mc-prop__label">{label}</span>
      {leading}
      <button
        ref={triggerRef}
        type="button"
        className="mc-prop__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={`${label}: ${shownLabel}`}
        disabled={disabled}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKeyDown}
      >
        <span className="mc-prop__value">{shownLabel}</span>
        <FiChevronDown size={12} className="mc-prop__chevron" aria-hidden="true" />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          id={listboxId}
          role="listbox"
          aria-label={label}
          className="mc-prop-menu"
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, minWidth: pos.minWidth, maxHeight: MENU_MAX_H }}
          onKeyDown={onKeyDown}
        >
          {items.map((item, i) => {
            if (item.kind === 'group') {
              return <div key={`g-${i}`} className="mc-prop-menu__group" role="presentation">{item.label}</div>;
            }
            optionIdx += 1;
            const idx = optionIdx;
            const isSelected = item.value === value;
            return (
              <div
                key={item.value}
                data-idx={idx}
                role="option"
                aria-selected={isSelected}
                className={`mc-prop-menu__item${isSelected ? ' mc-prop-menu__item--selected' : ''}${idx === activeIdx ? ' mc-prop-menu__item--active' : ''}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(item.value)}
              >
                {item.color && <span className="mc-prop-menu__dot" style={{ background: item.color }} aria-hidden="true" />}
                <span className="mc-prop-menu__text">
                  <span className="mc-prop-menu__label">{item.label}</span>
                  {item.hint && <span className="mc-prop-menu__hint">{item.hint}</span>}
                </span>
                {item.starred && <FiStar size={11} className="mc-prop-menu__star" aria-label="Primary agent" />}
                {isSelected && <FiCheck size={13} className="mc-prop-menu__check" aria-hidden="true" />}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default PropDropdown;
